import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import React from 'react';
import { theme } from '../../theme/theme';
import { Button } from './forms/Button';
import { motions } from './motions/motions';

const useTabsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    tabs: {
        display: 'flex',
        ':where(&)': { boxShadow: '0 10px 10px rgba(0, 0, 0, 0.1)' },
    },
    tab: {
        ':where(&)': {
            padding: '12px 10px',
            color: theme.colors.neutral[2],
            borderRadius: 0,
            '&:hover': { color: theme.colors.darkBlue[5] },
        },
    },
    tracker: {
        position: 'relative',
        ':where(&)': {
            height: 3,
            marginTop: -3,
            '& > span': {
                position: 'absolute',
                top: 0,
                bottom: 0,
                backgroundColor: theme.colors.darkBlue[5],
                transition: `all ${theme.transitions.default}s`,
            },
        },
    },
    pane: {
        display: 'flex',
        flex: 1,
    },
});

/**
 * Tabs component.
 * This component can be used as a controlled or uncontrolled input.
 *
 * @param labels tab labels
 * @param tabs tabs bar props
 * @param tabProps tab buttons props
 * @param trackerProps selected tab tracker props
 * @param paneProps tab content props
 * @param children contents of each tab
 * @param rootProps root container props
 */
export const Tabs = ({
    tab: controlledTab,
    setTab: controlledSetTab,
    tabsProps,
    tabProps,
    trackerProps,
    paneProps,
    labels,
    children,
    ...rootProps
}: React.ComponentProps<'div'> & {
    tab?: number;
    setTab?: (tab: number) => void;
    tabsProps?: React.ComponentProps<'div'>;
    tabProps?: React.ComponentPropsWithoutRef<'button'>;
    trackerProps?: React.ComponentProps<'div'>;
    paneProps?: HTMLMotionProps<'div'>;
    labels: React.ReactNode[];
    children: React.ReactNode[];
}) => {
    const classes = useTabsClasses();
    const [tab, setTab] = React.useState(controlledTab ?? 0);
    const tabs$ = React.useRef<HTMLDivElement>();
    const tracker$ = React.useRef<HTMLSpanElement>();
    const previous = React.useRef(tab);
    const direction = tab > previous.current ? 1 : -1;
    previous.current = tab;

    React.useLayoutEffect(
        () => void (controlledTab != undefined && tab !== controlledTab && setTab(controlledTab)),
        [tab, controlledTab],
    );

    React.useEffect(() => {
        const tabsRect = tabs$.current!.getBoundingClientRect();
        const tabRect = tabs$.current!.children[tab].getBoundingClientRect();
        tracker$.current?.style.setProperty('left', `${tabRect.left - tabsRect.left}px`);
        tracker$.current?.style.setProperty('right', `${tabsRect.right - tabRect.right}px`);
    }, [tab, tabs$.current, tracker$.current]);

    return (
        <div {...rootProps} className={clsx(classes.root, rootProps.className)}>
            <div ref={tabs$ as any} {...tabsProps} className={clsx(classes.tabs, tabsProps?.className)}>
                {labels.map((label, i) => (
                    <Button
                        key={i}
                        {...tabProps}
                        className={clsx(classes.tab, tabProps?.className, { open: i === tab })}
                        theme='light'
                        onClick={() => (setTab(i), controlledSetTab?.(i))}
                    >
                        {label}
                    </Button>
                ))}
            </div>
            <div {...trackerProps} className={clsx(classes.tracker, trackerProps?.className)}>
                <span ref={tracker$ as any} />
            </div>
            <AnimatePresence exitBeforeEnter>
                <motion.div
                    key={tab}
                    {...paneProps}
                    className={clsx(classes.pane, paneProps?.className)}
                    {...motions.presence({ slide: 10 * direction })}
                >
                    {children[tab]}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
